import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';

import { ShareOnTwitter } from 'components/Button/shareButton';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import { getConfig } from 'functions/getConfig';
import { executorDecompress } from 'functions/executorDecompress';
import {
  ContentContainer,
  ChallengeDescription,
  ChallengeLabel,
  SectionContainer,
  SectionColumn,
  RankListContainer,
  AuthorContainer,
  AuthorAvatar,
  AuthorName,
  MetaInfoRow,
  RatingAlignRow,
  SponsorLogo,
  ShareButtonContainer,
  RankingExplanationText,
} from './styles';
import * as actions from './actions';
import CurvedContainer, { CurvedContent, CurvedTitle } from '../../components/CurvedContainer';
import { Wrapper, Container, LinkButton } from '../../components';
import { RankList } from '../../components/rankList/rankList';
import Rating from '../../components/Rating';
import Tags from '../../components/Tags';
import CommentContainer from '../../components/comments/commentContainer';
import CourseContent from '../../components/CourseContent';

function decompressPlayback(gamePlay) {
  if (!gamePlay) return gamePlay;
  try {
    return gamePlay.map((match) => {
      return {
        participants: match.participants,
        levels: executorDecompress(match),
        finishedAt: match.finishedAt,
      };
    });
  } catch (e) {
    console.log('Error on decompressing match data: ', e);
  }
}

const config = getConfig();

export default function ChallengePage() {
  const { type, id } = useParams();

  const { error, selectedChallenge, tournamentPlaybacks } = useSelector((state) => state.challenge);
  const { token } = useSelector((state) => state.auth);

  const { getChallenge, setChallengeRating, getTournamentPlayback, resetState } = useActions(actions);

  const [challengeRating, setChallengeRatings] = useState(undefined);
  const [pixiLoadingStatus, setPixiLoadingStatus] = useState(false);
  const [tournamentGameDetails, setTournamentGameDetails] = useState(null);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (id) {
      getChallenge(id, type);
    }
  }, [type]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (id && token && challengeRating && challengeRating !== selectedChallenge?.currentRating) {
      setChallengeRating(
        challengeRating,
        type === 'exercises' || type === 'freecode' ? selectedChallenge._id : selectedChallenge.test
      );
    }
  }, [id, token, challengeRating, selectedChallenge, type]);

  useEffect(() => {
    if (selectedChallenge) {
      if (type !== 'courses') window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      setChallengeRatings(selectedChallenge?.currentRating || 0);
      setTournamentGameDetails(selectedChallenge.tournamentConfig);
      if (selectedChallenge.landingPageContent) {
        setDescription(selectedChallenge.landingPageContent.replaceAll('<p>', '<p style="word-break: break-word;">'));
      } else if (selectedChallenge.description) {
        setDescription(selectedChallenge.description.replaceAll('<p>', '<p style="word-break: break-word;">'));
      }
    }
  }, [selectedChallenge]);

  useEffect(() => {
    if (tournamentPlaybacks?.length) {
      setPixiLoadingStatus(false);
      setTournamentGameDetails(selectedChallenge?.tournamentConfig);
    } else {
      setTournamentGameDetails(null);
      setPixiLoadingStatus(false);
    }
  }, [tournamentPlaybacks]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  const onSelectedRow = (row) => {
    setPixiLoadingStatus(true);
    const params = {
      account: row.accountId,
      contest: id,
      limit: 20,
    };
    getTournamentPlayback(params);
  };

  const renderTopList = () =>
    !!selectedChallenge?.rankList?.length && (
      <Container column>
        <ChallengeLabel>Rank List</ChallengeLabel>
        {selectedChallenge.isTournament && (
          <RankingExplanationText>The ratings are updated nightly</RankingExplanationText>
        )}
        <ContentContainer center>
          <RankListContainer>
            <RankList
              key={selectedChallenge._id}
              onSelectedRow={onSelectedRow}
              isTournament={selectedChallenge.isTournament}
              rankList={selectedChallenge.rankList}
              gamePlay={tournamentPlaybacks && decompressPlayback(tournamentPlaybacks)}
              tournamentDetails={tournamentGameDetails}
              pixiLoadingStatus={pixiLoadingStatus}
              isOfficialChallenge={selectedChallenge.keywords ? selectedChallenge.keywords.includes('Official') : false}
            />
          </RankListContainer>
        </ContentContainer>
      </Container>
    );

  const renderDescription = () =>
    selectedChallenge && (
      <Container column>
        <ContentContainer>
          <MetaInfoRow>
            <SectionContainer>
              <SectionColumn>
                {selectedChallenge.keywords?.length > 0 && (
                  <div>
                    <ChallengeLabel>Tags</ChallengeLabel>
                    <Tags size={12} tags={selectedChallenge.keywords} singleLine />
                  </div>
                )}
                {selectedChallenge.author && (
                  <SectionContainer>
                    <ChallengeLabel>Author</ChallengeLabel>
                    <AuthorContainer>
                      <AuthorAvatar
                        image={
                          selectedChallenge.author.avatar
                            ? selectedChallenge.author.avatar === '/images/placeholder-avatar.png'
                              ? Images.defaultAvatar
                              : selectedChallenge.author.avatar
                            : Images.defaultAvatar
                        }
                      />
                      <AuthorName href={`/profile?id=${selectedChallenge.author._id}`}>
                        {selectedChallenge.author.name}
                      </AuthorName>
                    </AuthorContainer>
                  </SectionContainer>
                )}
              </SectionColumn>
            </SectionContainer>

            {selectedChallenge.sponsor && selectedChallenge.landingPageLogo && (
              <SectionContainer center>
                <SponsorLogo image={selectedChallenge.landingPageLogo} />
              </SectionContainer>
            )}

            <SectionContainer>
              {token && type !== 'courses' && (
                <RatingAlignRow>
                  <SectionColumn noGap>
                    <ChallengeLabel noMargin alignRight noWrap smallDevice>
                      Rate this{' '}
                      {selectedChallenge.category === 'exercise'
                        ? selectedChallenge.isFreecode
                          ? 'freecode'
                          : 'exercise'
                        : 'challenge'}
                    </ChallengeLabel>
                    <Rating
                      initialRating={challengeRating}
                      fractions={1}
                      size={16}
                      alignRight
                      onChange={(value) => setChallengeRatings(value)}
                    />
                  </SectionColumn>
                  <ShareButtonContainer>
                    <ShareOnTwitter url={window.location.href} title={selectedChallenge.textToShare} />
                  </ShareButtonContainer>
                </RatingAlignRow>
              )}
            </SectionContainer>
          </MetaInfoRow>
        </ContentContainer>
        <ContentContainer>
          <SectionContainer>
            <ChallengeLabel>Description</ChallengeLabel>
            <ChallengeDescription dangerouslySetInnerHTML={{ __html: description }} />
          </SectionContainer>
        </ContentContainer>
        {type !== 'courses' && (
          <LinkButton
            primary
            primaryGreenBorder
            href={
              type === 'exercises'
                ? `${config.REDIRECTION_URL}/exercise/${id}`
                : type === 'freecode'
                ? `${config.REDIRECTION_URL}/freecode/${id}`
                : `${config.REDIRECTION_URL}/challenge/${selectedChallenge._id}`
            }
          >
            Start
          </LinkButton>
        )}
      </Container>
    );

  return (
    <Wrapper color="transparent">
      <Helmet>
        <meta name="twitter:card" content="summary" />
        <meta name="title" content={selectedChallenge.name} />
        <meta property="og:title" content={selectedChallenge.name} />
        <meta property="twitter:title" content={selectedChallenge.name} />
        <meta name="description" content={description} />
        <meta property="og:description" content={description} />
        <meta property="twitter:description" content={description} />
        <meta name="image" content={selectedChallenge.image} />
        <meta property="og:image" content={selectedChallenge.image} />
        <meta property="twitter:image" content={selectedChallenge.image} />
      </Helmet>
      <CurvedContainer bottom height={500} color={''} image={selectedChallenge.image}>
        {id && (
          <CurvedContent keepCentered>
            <CurvedTitle>{selectedChallenge.name}</CurvedTitle>
            {type !== 'courses' && (
              <>
                <Rating
                  readonly
                  initialRating={selectedChallenge.averageRating}
                  size={18}
                  style={{ marginBottom: '10px' }}
                />

                <LinkButton
                  primary
                  removeMargin
                  href={
                    type === 'exercises'
                      ? `${config.REDIRECTION_URL}/exercise/${id}`
                      : type === 'freecode'
                      ? `${config.REDIRECTION_URL}/freecode/${id}`
                      : `${config.REDIRECTION_URL}/challenge/${selectedChallenge._id}`
                  }
                >
                  Start
                </LinkButton>
              </>
            )}
          </CurvedContent>
        )}
      </CurvedContainer>

      {renderDescription()}
      {type !== 'courses' && (
        <>
          {renderTopList()}

          <Container column>
            <ContentContainer>
              <SectionContainer>
                <ChallengeLabel>Discussion</ChallengeLabel>
                <CommentContainer
                  testId={type === 'challenges' ? selectedChallenge.test : id}
                  contestId={type === 'challenges' ? id : undefined}
                />
              </SectionContainer>
            </ContentContainer>
          </Container>
        </>
      )}
      {type === 'courses' && selectedChallenge.syllabus && <CourseContent course={selectedChallenge} />}
    </Wrapper>
  );
}
